import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';

import { trackApply } from '../analytics';
import { apiClient } from '../api/client';
import { useApi } from '../api/useApi';
import { AsyncBoundary } from '../components/AsyncState';
import { HtmlDocumentFrame } from '../components/HtmlDocumentFrame';
import { BackLink } from '../components/layout/BackLink';
import { RichText } from '../components/RichText';
import { ShareButton } from '../components/ShareButton';
import { StandLogo } from '../components/StandLogo';
import { FROM_SEARCH, paths, searchReturnPath } from '../routes';
import { formatDate } from '../utils/format';
import { isDesignedJobHtml } from '../utils/job-html';
import { accentBackground, accentFor } from '../utils/pavilion-accent';

export function JobDetailsPage(): ReactElement {
  const { t } = useTranslation();
  const { standSlug, jobId } = useParams<{ standSlug: string; jobId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const from = searchParams.get('from') ?? undefined;
  const fromSearch = from === FROM_SEARCH;
  const parsed = Number(jobId);
  const valid = Number.isInteger(parsed);
  // A non-numeric id redirects below before anything renders, so it must not go
  // out as a request for `/offers/NaN`.
  const offer = useApi(
    () => (valid ? apiClient.getOffer(parsed) : new Promise<never>(() => undefined)),
    [parsed],
  );

  if (!valid) {
    return <Navigate to="/" replace />;
  }

  // Only a resolved 404 means the offer is gone; redirecting any earlier would
  // bounce away while the first request is still in flight.
  if (!offer.loading && offer.error?.status === 404) {
    return <Navigate to="/" replace />;
  }

  const job = offer.data;
  const pavilion = job?.pavilions[0];

  // The job id alone identifies the offer; the exhibitor in the path is for
  // people reading the URL. A link naming the wrong one — the offer moved, or
  // the URL was edited — or none at all (`/jobs/:jobId`) is swapped for the
  // right URL.
  if (job !== undefined && job.stand.slug !== standSlug) {
    return (
      <Navigate
        to={paths.job(job.stand.slug, job.id, from)}
        state={location.state as unknown}
        replace
      />
    );
  }

  return (
    <div className="pb-28">
      <AsyncBoundary loading={offer.loading} error={offer.error} onRetry={offer.reload}>
        {job !== undefined && (
          <>
            <div className="app-shell">
              {fromSearch ? (
                <BackLink to={searchReturnPath(location.state)} label={t('search.title')} />
              ) : (
                <BackLink
                  // A pavilion `from` goes back to the stand, whose own back
                  // link then returns to that pavilion.
                  to={paths.exhibitor(job.stand.slug, {
                    tab: 'jobs',
                    ...(from !== undefined && { from }),
                  })}
                  label={t('job.backToJobs')}
                  suffix={job.stand.title}
                />
              )}

              {/* `hideHeader` lets an exhibitor supply artwork that already carries the
                  title, so the framed header would duplicate it. */}
              {!job.hideHeader && (
                <header className="relative mt-2 mb-6 pl-5">
                  {pavilion !== undefined && (
                    <span
                      aria-hidden="true"
                      className={`absolute top-1 bottom-1 left-0 w-1 rounded-r ${accentBackground(
                        accentFor(pavilion.position),
                      )}`}
                    />
                  )}

                  <div className="flex items-start justify-between gap-3">
                    {/* Always shown, whatever `hideTitle` says: it defaults to 1
                        and only ever hid the legacy app's separate heading above
                        the ad, not an intro like this one. */}
                    {job.title !== null && (
                      <h1 className="text-2xl font-bold text-fog-100">{job.title}</h1>
                    )}
                    <ShareButton
                      title={
                        job.title === null ? job.stand.title : `${job.title} – ${job.stand.title}`
                      }
                      path={paths.job(job.stand.slug, job.id)}
                      contentType="job"
                      itemId={job.id}
                      className="mt-1 ml-auto"
                    />
                  </div>

                  {job.location !== null && (
                    <p className="mt-2 text-sm text-fog-500">
                      {t('job.location')} {job.location}
                    </p>
                  )}
                  {job.deadline !== null && (
                    <p className="text-sm text-fog-500">
                      {t('job.deadline')} {formatDate(job.deadline)}
                    </p>
                  )}

                  <Link
                    to={paths.exhibitor(job.stand.slug)}
                    className="mt-4 flex items-center gap-2"
                  >
                    <StandLogo url={job.stand.logoUrl} className="size-8" />
                    <span className="text-sm font-semibold text-fog-300">{job.stand.title}</span>
                  </Link>
                </header>
              )}
            </div>

            {/* A designed ad is shown as authored, framed on white; a plain one
                takes the app's typography. See utils/job-html.ts. */}
            {job.descriptionHtml !== null && job.descriptionHtml.trim() !== '' && (
              <div className="app-shell">
                {isDesignedJobHtml(job.descriptionHtml) ? (
                  <HtmlDocumentFrame
                    html={job.descriptionHtml}
                    title={job.title ?? job.stand.title}
                    className="overflow-hidden rounded-xl"
                  />
                ) : (
                  <RichText html={job.descriptionHtml} className="text-fog-300" />
                )}
              </div>
            )}

            {job.applyLink !== null && (
              // Sticky so the primary action stays reachable through a long description.
              <div className="fixed inset-x-0 bottom-0 z-30 border-t border-ink-800 bg-ink-950/95 backdrop-blur">
                <div className="app-shell py-3">
                  <a
                    href={job.applyLink}
                    target="_blank"
                    rel="noreferrer noopener"
                    onClick={() => {
                      trackApply();
                    }}
                    className="block rounded-xl bg-primary py-3.5 text-center text-lg font-semibold text-white"
                  >
                    {job.applyLinkTitle ?? t('job.apply')}
                  </a>
                </div>
              </div>
            )}
          </>
        )}
      </AsyncBoundary>
    </div>
  );
}
